import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";

import { Button, EmptyState, ErrorState, Notice, PageHeader, SkeletonRows, Status, Surface, cn, focusRing } from "../../../../components/ui";
import { businessApi } from "../../../../services/api";
import { useBusinessAuth } from "../../../context/BusinessAuthContext";
import PayslipDocument from "../payslips/PayslipDocument";
import RaiseCorrectionDialog from "../adjustments/RaiseCorrectionDialog";
import { HR_CORRECTIONS_PATH, adjustmentsApi, classificationLabel, stateOf } from "../adjustments/adjustmentView";
import { payDate, periodDates, periodLabel } from "../payslips/payslipView";
import { HR_PAYROLL_BASE, periodPath, shortDate } from "./hrPayroll";

// HR › Payroll › one stored payslip (APP-HR-108). Read-only. Every figure is the one saved when the payroll was
// calculated: the server returns the payslip row, its deduction lines, its contribution lines and its paid holiday
// days. Nothing here is worked out again from today's attendance, pay or rules. The server gives it only to someone
// who may read payroll in this business; another business's payslip, or none, reads as missing.

const lower = (value) => String(value || "").toLowerCase();

const STATE = {
  draft: { label: "Calculated, not yet approved", tone: "neutral" },
  approved: { label: "Approved by Finance, not yet released", tone: "neutral" },
  released: { label: "Released to the employee", tone: "neutral" },
  settled: { label: "Paid", tone: "success" },
  voided: { label: "Voided", tone: "neutral" },
};

/** Where the salary of this payslip stands, in HR's words, from the stored payslip and its Finance record. */
export function recordPayment(payslip) {
  const status = lower(payslip?.payslip_storage_status || payslip?.status);
  const record = lower(payslip?.payout_record_status);
  if (status === "voided") return { label: "Voided", tone: "neutral", detail: "This payslip was voided. It pays nothing." };
  if (record === "settled" || status === "settled") {
    return { label: "Paid", tone: "success", date: shortDate(payslip?.payout_settled_at), detail: "Finance recorded this salary as paid." };
  }
  if (record === "partially_settled") return { label: "Partly paid", tone: "neutral", detail: "Finance recorded part of this salary as paid." };
  if (status === "released") {
    return Number(payslip?.net_pay_cents || 0) === 0
      ? { label: "No cash due", tone: "neutral", detail: "Net pay is ₱0, so there is no cash to hand over." }
      : { label: "Payment pending", tone: "neutral", detail: "Finance has not recorded paying this salary yet." };
  }
  return { label: "Not payable yet", tone: "neutral", detail: "A salary is paid after its payslip is released." };
}

const PAGE_RULE = "@page { size: auto; margin: 15mm; }";

export default function HRPayslip() {
  const { payslipId } = useParams();
  const auth = useBusinessAuth();
  const [state, setState] = useState({ status: "loading", payslip: null });
  // Report 54B (D2): a paid payslip is corrected by a separate, linked correction; the payslip is never edited.
  const navigate = useNavigate();
  const [raising, setRaising] = useState(false);
  const [corrections, setCorrections] = useState([]);
  const canManage = String(auth?.role || "").toUpperCase() === "OWNER" || Boolean(auth?.hasPermission?.("hr:payroll:manage"));
  useEffect(() => {
    let live = true;
    adjustmentsApi.list({ payslip_id: payslipId }).then((rows) => { if (live) setCorrections(Array.isArray(rows) ? rows : []); }).catch(() => {});
    return () => { live = false; };
  }, [payslipId]);

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-payslip-paper", "");
    style.textContent = PAGE_RULE;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const load = useCallback(async () => {
    try {
      const result = await businessApi.getPayslipDetails(payslipId);
      const payslip = result?.payslip || result?.data?.payslip || result?.data || null;
      setState(payslip?.id ? { status: "ready", payslip } : { status: "missing", payslip: null });
    } catch (error) {
      const code = error?.response?.status ?? error?.status;
      setState({ status: code === 404 || code === 403 ? "missing" : "error", payslip: null });
    }
  }, [payslipId]);

  useEffect(() => {
    setState({ status: "loading", payslip: null });
    load();
  }, [load]);

  const { status, payslip } = state;
  const backTo = payslip?.payroll_period_id ? periodPath(payslip.payroll_period_id) : HR_PAYROLL_BASE;
  const back = (
    <Button as={Link} to={backTo} variant="quiet" size="sm" icon={ArrowLeft} className="-ml-3 mb-2 print:hidden">
      {payslip?.payroll_period_id ? "Back to the payroll" : "All payroll"}
    </Button>
  );

  if (status !== "ready") {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <div>
          {back}
          <PageHeader title="Payslip" />
        </div>
        <Surface>
          {status === "loading" ? (
            <SkeletonRows rows={6} columns={2} label="Loading the payslip" />
          ) : status === "missing" ? (
            <EmptyState
              title="This payslip isn't available"
              description="It may belong to another business, or the link is wrong."
              action={<Button as={Link} to={HR_PAYROLL_BASE}>Open Payroll</Button>}
            />
          ) : (
            <ErrorState title="The payslip couldn't be loaded" description="Check your connection and try again. Nothing has changed." onRetry={load} />
          )}
        </Surface>
      </div>
    );
  }

  const stored = STATE[lower(payslip.payslip_storage_status || payslip.status)] || { label: "Calculated", tone: "neutral" };
  const dates = periodDates(payslip);
  const period = periodLabel(payslip);
  const name = payslip.member_name || "Employee";

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <div>
        {back}
        <PageHeader
          title={`Payslip — ${name}`}
          description={[period, dates !== period ? dates : null, payDate(payslip) ? `pay date ${payDate(payslip)}` : null].filter(Boolean).join(" · ")}
          meta={(
            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <Status tone={stored.tone} className="font-medium">{stored.label}</Status>
              {payslip.payslip_number ? <span className="tabular-nums"><span aria-hidden="true">· </span>{payslip.payslip_number}</span> : null}
              {payslip.business_member_id ? (
                <span>
                  <span aria-hidden="true">· </span>
                  <Link to={`/business/hr/employees/${payslip.business_member_id}/payslips`} className={cn("rounded-control font-medium text-accent hover:underline print:hidden", focusRing)}>
                    {name}'s payslips
                  </Link>
                </span>
              ) : null}
            </span>
          )}
          actions={(
            <div className="flex flex-wrap gap-2 print:hidden">
              {canManage && lower(payslip.payslip_storage_status || payslip.status) === "settled" ? <Button onClick={() => setRaising(true)}>Raise a correction</Button> : null}
              <Button icon={Printer} onClick={() => window.print()}>Print payslip</Button>
            </div>
          )}
        />
      </div>

      <Notice title="The stored payslip" className="print:hidden">
        These are the figures saved when this payroll was calculated, in the form the employee sees them. Later changes to
        attendance, pay or payroll rules do not change a stored payslip.
      </Notice>

      {corrections.length ? (
        <Surface padded className="print:hidden">
          <h2 className="text-section-title text-foreground">Corrections to this payslip</h2>
          <p className="text-body-sm text-secondary">Each is a separate record with its own approval and payment. The payslip below is unchanged.</p>
          <ul className="mt-2 divide-y divide-subtle text-body-sm">
            {corrections.map((item) => (
              <li key={item.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                <Link to={`${HR_CORRECTIONS_PATH}/${item.id}`} className={cn("rounded-control font-medium text-accent hover:underline", focusRing)}>{classificationLabel(item.classification)} · {item.number}</Link>
                <Status tone={stateOf(item).tone}>{stateOf(item).label}</Status>
              </li>
            ))}
          </ul>
        </Surface>
      ) : null}

      {raising ? (
        <RaiseCorrectionDialog payslip={payslip} onClose={() => setRaising(false)} onCreated={(created) => { setRaising(false); if (created?.id) navigate(`${HR_CORRECTIONS_PATH}/${created.id}`); }} />
      ) : null}

      <PayslipDocument
        payslip={payslip}
        businessName={auth.businessName}
        employee={[name, payslip.employee_code].filter(Boolean).join(" · ")}
        payslipState={stored.label}
        payment={recordPayment(payslip)}
      />
    </div>
  );
}
